import { Link } from "react-router-dom";
import { Check, ArrowRight, GraduationCap, PhoneCall, Home as HomeIcon, Syringe } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { SITE, IMG, BEHAVIOURS } from "../data/siteData";

const tiers = [
  {
    name: "2-Week Jumpstart Program",
    tag: "Dogs 9 months & under",
    price: "$1,950",
    points: [
      "On-leash obedience: sit, down, come, heel (loose leash walking), place and stay",
      "Socialization with dogs, humans and new environments",
      "Basic manners and healthy habits for young dogs",
      "Not suitable for dogs with behaviour concerns",
    ],
  },
  {
    name: "4-Week Off-Leash Program",
    tag: "On & off-leash reliability",
    price: "$4,000+",
    points: [
      "All previously mentioned commands, on and off-leash",
      "Socialization and exposure in new environments",
      "Addresses barking, jumping, pulling on leash and reactivity/over-arousal",
      "Not suitable for dogs with aggression concerns",
    ],
  },
  {
    name: "Behaviour Modification Program",
    tag: "4 to 6+ weeks",
    price: "$4,000 – $6,000",
    points: [
      "Focused on rehabilitation of serious behaviour concerns",
      "Aggression (human and/or dog), separation anxiety, severe reactivity",
      "Fully customized to your dog's issues and your goals",
      "Longer programs recommended for more severe cases",
    ],
  },
];

export default function BoardAndTrain() {
  return (
    <div data-testid="board-train-page">
      <SEO
        title="Board & Train Programs | Chinook K9 Dog Training Calgary"
        description="Immersive 2-6 week board and train programs near Calgary. Obedience, off-leash reliability and behaviour modification for aggression, anxiety and reactivity."
        path="/services/board-and-train"
      />

      <section className="relative min-h-[70vh] flex items-end overflow-hidden" data-testid="bt-hero">
        <img src={IMG.boardTrainWide} alt="Board and train program at Chinook K9" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-black/25 to-black/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 pt-40 w-full">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4 hero-text-shadow">Board & Train Program</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white max-w-3xl leading-[1.05] hero-text-shadow">
              The most effective way to train your dog
            </h1>
            <p className="mt-5 text-white/85 max-w-2xl leading-relaxed hero-text-shadow">
              Programs tailored to your individual goals, covering the widest range of education and behaviour
              modification for any breed and any issue.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-24" data-testid="bt-what">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6">What is the Board and Train Program?</h2>
            <div className="space-y-4 text-white/70 leading-relaxed">
              <p>During our 2-6 week Board & Train programs your dog will receive round-the-clock training and care by our professional trainers and experienced staff — allowing us to tackle everything from basic obedience and socialization to advanced off-leash obedience and behaviour modification.</p>
              <p>Our large indoor training facility and outdoor acreage provides an environment conducive to your dog's learning. They receive one-on-one education daily, as well as structured socialization with other dogs, humans, and exposure to new environments during their stay.</p>
              <p>The length of program depends on the issues at hand and your goals. Whether you're starting on the right foot with a new dog, or dealing with severe behaviour issues like reactivity and aggression — we are here to help you live the life you've dreamed of with your dog.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h3 className="font-heading text-xl font-bold text-white mb-5">We focus on far more than obedience</h3>
            <p className="text-white/70 leading-relaxed mb-7">
              We emphasize the dog-owner relationship, socialization and exposure to different environments and stimulus,
              proper socialization with other dogs and animals, and calm, comfortable living inside your home — while
              ensuring your dog's obedience in any environment.
            </p>
            <h4 className="font-heading text-sm font-bold tracking-widest uppercase text-brand mb-4">Behaviours we can assist with</h4>
            <div className="grid grid-cols-2 gap-2.5" data-testid="bt-behaviours">
              {BEHAVIOURS.map((b) => (
                <div key={b} className="flex items-center gap-2 text-sm text-white/75">
                  <Check size={15} className="text-brand shrink-0" /> {b}
                </div>
              ))}
            </div>
            <p className="text-white/40 text-xs mt-5">*Longer programs are recommended for more severe behaviour issues; we will advise on the length that best fits your situation and budget.</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 text-slate-900" data-testid="bt-tiers">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-12 max-w-2xl">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Programs</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Choose the right program length</h2>
            <p className="mt-4 text-slate-600">Contact us for a consultation — we'll recommend the program that best fits your dog and your goals.</p>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {tiers.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <div className="border border-black/10 rounded-2xl p-8 h-full flex flex-col hover:border-brand/50 hover:shadow-xl transition-[border-color,box-shadow]" data-testid={`bt-tier-${i}`}>
                  <span className="text-xs font-heading font-bold tracking-widest uppercase text-brand">{t.tag}</span>
                  <h3 className="font-heading text-xl font-bold mt-2 mb-1">{t.name}</h3>
                  <p className="font-heading text-2xl font-extrabold text-brand mb-5" data-testid={`bt-price-${i}`}>{t.price || "Contact for pricing"}</p>
                  <ul className="space-y-3 flex-1">
                    {t.points.map((p) => (
                      <li key={p} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
                        <Check size={16} className="text-brand shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="mt-7 inline-flex items-center gap-2 text-brand font-heading font-bold text-sm hover:gap-3 transition-all" data-testid={`bt-tier-cta-${i}`}>
                    Request Details <ArrowRight size={15} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-obsidian py-24" data-testid="bt-owner-education">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="max-w-3xl mb-12">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Owner Education & Go-Home Plan</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Your dog's success continues at home
            </h2>
            <p className="mt-4 text-white/70 leading-relaxed">
              Your dog will receive an enormous amount of education, life experience and training during their stay —
              however it will be up to you, the owner, to ensure your dog's success in the days, weeks and months after they return home.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: GraduationCap, title: "Included Sessions", desc: "One (1) Mid-Way Session (more based on program length), one (1) Go-Home session (1-2 hours at pick-up), and three (3) Follow-Up Sessions. We break down all training and ensure you're confident continuing at home." },
              { icon: HomeIcon, title: "Go-Home Guide & Equipment", desc: "Every client receives a Go-Home Guide for a successful transition, plus the training equipment used during your dog's stay — including an e-collar, prong collar or slip lead, and a place bed. We are an E-Collar Technologies authorized dealer." },
              { icon: PhoneCall, title: "Continued Support", desc: "Ongoing support via phone, text or email after your program, plus discounts on follow-up training, day training, and reduced-cost boarding for program graduates." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 h-full" data-testid={`bt-edu-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-brand/15 text-brand flex items-center justify-center mb-5"><c.icon size={22} /></div>
                  <h3 className="font-heading text-lg font-bold text-white mb-3">{c.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#121212] border-y border-white/10 py-20" data-testid="bt-boarding-req">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <Syringe className="text-brand" size={26} />
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">Boarding Requirements</h2>
            </div>
            <p className="text-white/70 leading-relaxed mb-5">
              Proof of current vaccinations is required before boarding your dog. Vaccinations must be current for your dog's age:
            </p>
            <ul className="space-y-2.5">
              {["Rabies vaccine", "Bordetella", "Distemper (DHLPP)"].map((v) => (
                <li key={v} className="flex items-center gap-2.5 text-white/80 font-medium"><Check size={16} className="text-brand" /> {v}</li>
              ))}
            </ul>
            <p className="text-white/50 text-sm mt-5">
              The minimum age for our Board and Train Program is 6 months old. Have a younger puppy? Check out our{" "}
              <Link to="/services/private-training" className="text-brand hover:underline">Private Training Program</Link>.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-obsidian border border-white/10 rounded-2xl p-9 text-center">
              <h3 className="font-heading text-2xl font-bold text-white mb-3">Ready to get started?</h3>
              <p className="text-white/60 text-sm mb-7">Call or email for questions about our Board and Train Program or to book your dog's stay.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" data-testid="bt-contact-cta" className="bg-brand text-white font-heading font-bold px-8 py-3.5 rounded-full hover:bg-red-700 transition-colors">Contact Us</Link>
                <a href={SITE.phoneHref} data-testid="bt-call-cta" className="border border-white/25 text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors">{SITE.phone}</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
